import React, { forwardRef, useImperativeHandle } from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { Col, Form, Row, Label } from 'reactstrap';
import OInput from '../../../../Components/OElements/OInput';
import OSelect from '../../../../Components/OElements/OSelect';
import { DateInput } from '@mantine/dates';
import { getWorkOrderStatusList } from '../../../../helpers/work_order_status/api_helper';
import { getReportingTypeList } from '../../../../helpers/reporting_type/api_helper';
import { getReportingFrequencyList } from '../../../../helpers/reporting_frequency/api_helper';
import { getMeasurementFrequencyList } from '../../../../helpers/measurement_frequency/api_helper';
import { getWaterRightPositionList } from '../../../../helpers/water_right_position/api_helper';

const WorkForm = forwardRef(({ onSubmit, dataToEdit, waterRightData }, ref) => {
	const isEditMode = !!dataToEdit;

	const validation = useFormik({
		enableReinitialize: true,
		initialValues: {
			work_order_code: dataToEdit?.work_order_code || '',
			work_order_status_id: dataToEdit?.work_order_status_id || '',
			reporting_type_id: dataToEdit?.reporting_type_id || '',
			reporting_frequency_id: dataToEdit?.reporting_frequency_id || '',
			measurement_frequency_id: dataToEdit?.measurement_frequency_id || '',
			position_id: dataToEdit?.position_id || '',
			reporting_start_date: dataToEdit?.reporting_start_date || null,
			work_proof: null,
			water_right_id: waterRightData?._id || '',
		},
		validationSchema: Yup.object({
			work_order_code: Yup.string().required('El código de obra es requerido'),
			work_order_status_id: Yup.string().required('El estado de obra es requerido'),
			reporting_type_id: Yup.string().required('El tipo de reportabilidad es requerido'),
			reporting_frequency_id: Yup.string().required('La frecuencia de reportabilidad es requerida'),
			measurement_frequency_id: Yup.string().required('La frecuencia de mediciones es requerida'),
			position_id: Yup.string().required('La posición es requerida'),
			reporting_start_date: Yup.date().required('La fecha de inicio de reportabilidad es requerida').nullable(),
			work_proof: Yup.mixed().nullable(),
		}),
		onSubmit: values => {
			onSubmit(values);
		},
	});

	useImperativeHandle(ref, () => ({
		handleSubmit: () => {
			validation.handleSubmit();
		},
	}));

	return (
		<Form
			onSubmit={e => {
				e.preventDefault();
				validation.handleSubmit();
				return false;
			}}
		>
			<Row>
				<Col md={6} className='mb-3'>
					<OInput
						label='Código de Obra'
						name='work_order_code'
						type='text'
						placeholder='Ingrese el código de obra'
						validation={validation}
						isRequired={true}
					/>
				</Col>
				<Col md={6} className='mb-3'>
					<OSelect
						label='Estado de Obra'
						name='work_order_status_id'
						value={validation.values.work_order_status_id}
						validation={validation}
						isRequired={true}
						value_key='_id'
						requestApi={async () => {
							const response = await getWorkOrderStatusList();
							return response.data || response;
						}}
					/>
				</Col>
			</Row>

			<Row>
				<Col md={6} className='mb-3'>
					<OSelect
						label='Tipo de Reportabilidad'
						name='reporting_type_id'
						value={validation.values.reporting_type_id}
						validation={validation}
						isRequired={true}
						value_key='_id'
						requestApi={async () => {
						const response = await getReportingTypeList();
						return response.data || response;
						}}
					/>
				</Col>
				<Col md={6} className='mb-3'>
					<OSelect
						label='Frecuencia de Reportabilidad'
						name='reporting_frequency_id'
						value={validation.values.reporting_frequency_id}
						validation={validation}
						isRequired={true}
						value_key='_id'
						requestApi={async () => {
						const response = await getReportingFrequencyList();
						return response.data || response;
						}}
					/>
				</Col>
			</Row>

			<Row>
				<Col md={6} className='mb-3'>
					<OSelect
						label='Frecuencia de Mediciones'
						name='measurement_frequency_id'
						value={validation.values.measurement_frequency_id}
						validation={validation}
						isRequired={true}
						value_key='_id'
						requestApi={async () => {
							const response = await getMeasurementFrequencyList();
							return response.data || response;
						}}
					/>
				</Col>
				<Col md={6} className='mb-3'>
					<OSelect
						label='Posición'
						name='position_id'
						value={validation.values.position_id}
						validation={validation}
						isRequired={true}
						value_key='_id'
						requestApi={async () => {
							const response = await getWaterRightPositionList();
							return response.data || response;
						}}
					/>
				</Col>
			</Row>

			<Row>
				<Col md={6} className='mb-3'>
					<div className='mb-3'>
						<Label htmlFor='reporting_start_date' className='form-label'>
							Fecha de Inicio de Reportabilidad <span className='text-danger'>*</span>
						</Label>
						<DateInput
							id='reporting_start_date'
							name='reporting_start_date'
							placeholder='Seleccione la fecha'
							value={validation.values.reporting_start_date ? new Date(validation.values.reporting_start_date) : null}
							onChange={(date) => validation.setFieldValue('reporting_start_date', date)}
							onBlur={() => validation.setFieldTouched('reporting_start_date', true)}
							error={
								validation.touched.reporting_start_date && validation.errors.reporting_start_date
									? validation.errors.reporting_start_date
									: null
							}
							clearable
						/>
					</div>
				</Col>
			</Row>

			<Row>
				<Col md={12} className='mb-3'>
					<div className='mb-3'>
						<Label className='form-label'>
							Comprobante de la Obra - PDF {!isEditMode && <span className='text-danger'>*</span>}
						</Label>
						{isEditMode && dataToEdit?.file && (
							<div className='mb-2'>
								<small className='text-muted'>
									Archivo actual: {dataToEdit.file.filename || 'comprobante_obra.pdf'}
								</small>
							</div>
						)}
						<input
							id='work_proof'
							name='work_proof'
							type='file'
							accept='application/pdf'
							className={`form-control ${validation.touched.work_proof && validation.errors.work_proof ? 'is-invalid' : ''}`}
							onChange={(event) => {
								validation.setFieldValue('work_proof', event.currentTarget.files[0]);
							}}
							onBlur={() => validation.setFieldTouched('work_proof', true)}
						/>
						{validation.touched.work_proof && validation.errors.work_proof && (
							<div className='invalid-feedback d-block'>
								{validation.errors.work_proof}
							</div>
						)}
						<small className='text-muted'>Formato permitido: PDF</small>
					</div>
				</Col>
			</Row>
		</Form>
	);
});

WorkForm.displayName = 'WorkForm';

export default WorkForm;
